import { Divider, Stack, Typography } from '@mui/material';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import InfoTooltip from './info-tooltip';
import type { ManagementHybrid } from './use-management-data';

function Figure({ label, value }: { label: string; value: string }) {
    return (
        <Stack>
            <Typography variant='subtitle2' color='primary'>
                {label}
            </Typography>
            <Typography variant='body1' fontWeight='bold' color='primary'>
                {value}
            </Typography>
        </Stack>
    );
}

function SectionLabel({ children, action }: { children: string; action?: React.ReactNode }) {
    return (
        <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
            <Typography variant='overline' color='primary.onSurface' textTransform='uppercase'>
                {children}
            </Typography>
            {action}
        </Stack>
    );
}

const NOT_AVAILABLE = '-';

/** Density, seed and bag figures for one hybrid. */
function HybridDensityCard({
    hybrid,
    planterEfficiency,
    areaInHectares,
}: {
    hybrid: ManagementHybrid;
    planterEfficiency: number;
    areaInHectares?: number;
}) {
    const units = useUnitProvider();

    // Precision is left to the converter: whole numbers for pl/ha, one decimal for pl/m².
    const harvestDensity = hybrid.harvestDensity ? units.toCornDensity(hybrid.harvestDensity, undefined) : undefined;
    const plantingDensity = hybrid.plantingDensity
        ? units.toCornDensity(hybrid.plantingDensity, undefined, 'planting')
        : undefined;
    const estimatedYield = hybrid.estimatedYield ? units.toCornYield(hybrid.estimatedYield, undefined) : undefined;
    const bagsPerHectare = hybrid.bagsPerHectare ? units.toBags(hybrid.bagsPerHectare, 2) : undefined;

    return (
        <Stack
            sx={{
                height: '100%',
                borderRadius: '4px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: (theme) => theme.palette.divider,
            }}
        >
            <Stack p={2} spacing={1} sx={{ background: (theme) => theme.palette.primary.infoBackground }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                    <Stack
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: hybrid.color,
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant='h6' color='primary'>
                        {hybrid.catalogName}
                    </Typography>
                </Stack>
                <SectionLabel>{_('Plantas a cosecha')}</SectionLabel>
                <Typography variant='h5' color='primary'>
                    {harvestDensity ? `${harvestDensity.localeValue} ${harvestDensity.unit}` : NOT_AVAILABLE}
                </Typography>
                <Stack direction='row' spacing={3}>
                    <Figure
                        label={_('Rendimiento')}
                        value={estimatedYield ? `${estimatedYield.localeValue} ${estimatedYield.unit}` : NOT_AVAILABLE}
                    />
                    <Figure
                        label={_('Nitrógeno')}
                        value={
                            hybrid.nitrogen !== undefined
                                ? `${hybrid.nitrogen.toFixed(2)} ${units.getUnit('weight/area', 'corn')}`
                                : NOT_AVAILABLE
                        }
                    />
                </Stack>
            </Stack>

            <Stack p={2} spacing={1}>
                <SectionLabel
                    action={
                        <InfoTooltip
                            title={_(
                                'La densidad de siembra está calculada con un porcentaje de logro del {{value}}%',
                                {
                                    value: planterEfficiency,
                                }
                            )}
                        />
                    }
                >
                    {_('Semillas a la siembra')}
                </SectionLabel>
                <Typography variant='h5' color='primary'>
                    {plantingDensity ? `${plantingDensity.localeValue} ${plantingDensity.unit}` : NOT_AVAILABLE}
                </Typography>
            </Stack>

            <Divider />

            <Stack p={2} spacing={1}>
                <SectionLabel>{_('Bolsas totales')}</SectionLabel>
                <Typography variant='h5' color='primary'>
                    {hybrid.totalBags !== undefined
                        ? _('{{count}} bolsas', { count: hybrid.totalBags })
                        : NOT_AVAILABLE}
                </Typography>
                <Stack direction='row' spacing={3} flexWrap='wrap' useFlexGap>
                    <Figure
                        label={`${_('Bolsas')}/${units.getUnit('area', 'corn')}`}
                        value={bagsPerHectare ? bagsPerHectare.localeValue : NOT_AVAILABLE}
                    />
                    <Figure
                        label={hybrid.catalogName}
                        value={hybrid.hybridBags !== undefined ? String(hybrid.hybridBags) : NOT_AVAILABLE}
                    />
                    <Figure
                        label={_('Refugio')}
                        value={hybrid.refugeBags !== undefined ? String(hybrid.refugeBags) : NOT_AVAILABLE}
                    />
                </Stack>
                {areaInHectares === undefined && (
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('Definí la superficie del lote para calcular las bolsas.')}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default HybridDensityCard;
